import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import MuiTouchRipple, { type TouchRippleActions, type TouchRippleProps } from '@mui/material/ButtonBase/TouchRipple';

/** MUI's TouchRipple.d.ts types the default export as a `ForwardRefRenderFunction`, but at runtime it's a forwardRef component. */
const TouchRipple = MuiTouchRipple as unknown as React.ForwardRefExoticComponent<TouchRippleProps & React.RefAttributes<TouchRippleActions>>;
/** TouchRipple's enter animation duration. */
const RIPPLE_EXPAND_MS = 550;

export type LockRippleHandle = { play: () => void };

/** Fills its nearest positioned ancestor and plays a button-style ripple across it from the center on `play()`. */
export const LockRipple = forwardRef<LockRippleHandle>(function LockRipple(_props, ref) {
	const rootRef = useRef<HTMLDivElement | null>(null);
	const rippleRef = useRef<TouchRippleActions>(null);

	useImperativeHandle(ref, () => ({
		play: () => {
			const rect = rootRef.current?.getBoundingClientRect();
			if (!rect) return;
			// A pointer position (rather than `center: true`) makes TouchRipple size the ripple to reach the
			// farthest corner; `center: true` uses a smaller size meant for icon buttons.
			rippleRef.current?.start({ clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2 } as React.MouseEvent);
			// Released only once the ripple has fully expanded, since TouchRipple fades it out from release.
			setTimeout(() => rippleRef.current?.stop(), RIPPLE_EXPAND_MS);
		},
	}), []);

	return (
		<Box
			ref={rootRef}
			aria-hidden
			sx={{
				position: 'absolute',
				inset: 0,
				pointerEvents: 'none',
				color: (theme) => alpha(theme.palette.primary.light, 0.2),
			}}
		>
			<TouchRipple ref={rippleRef} />
		</Box>
	);
});
